// Clean engine metadata from audit descriptions
const cleanDescription = (text: string): string => {
  if (!text) return text;
  if (/workflow skill:|Matching complete:|Detected by workflow|exceptions out of|records scanned/i.test(text)) {
    return text
      .replace(/Detected by workflow skill: \w+\.\s*/i, '')
      .replace(/Matching complete:.*$/i, '')
      .replace(/Detected \d+ \w+ exceptions? out of \d+ records? scanned\.?\s*/i, 'Exception detected. ')
      .replace(/Exception detected:\s*Detected\s+/i, 'Exception detected: ')
      .replace(/:\s*Unknown\s*$/i, '')
      .trim() || 'Exception detected';
  }
  return text;
};

export default function AuditTrail({ entries }: { entries: any[] }) {
  if (!entries || entries.length === 0) return null;

  const STATUS_COLORS: Record<string, string> = {
    detected: '#64748b', classifying: 'var(--navy-600)', classified: 'var(--navy-600)',
    reviewing: 'var(--warning)', approved: '#10b981', rejected: '#ef4444',
    closed: '#6b7280', escalated: 'var(--navy-800)', processing: 'var(--accent)',
  };

  const ACTION_ICONS: Record<string, string> = {
    detected: '\u25CF', flagged_for_review: '\u25B2', resolution_generated: '\u2726',
    je_posted: '\u2713', human_approved: '\u2713', human_rejected: '\u2717',
    escalated: '\u25B4', auto_executed: '\u26A1', investigation_started: '\u25C6',
    approved_after_investigation: '\u2713', resolution_executed: '\u2726',
  };

  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', marginBottom: 10, letterSpacing: 1, textTransform: 'uppercase' }}>
        Audit Trail (SOX Compliant)
        <span style={{ fontWeight: 400, marginLeft: 8, letterSpacing: 0 }}>{entries.length} entries</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {entries.map((a: any, i: number) => {
          const actionKey = (a.action || '').toLowerCase();
          const isJE = actionKey === 'je_posted';
          const isResolution = actionKey.includes('resolution');
          const isApproval = actionKey.includes('approved') || actionKey.includes('rejected');
          const isEscalation = actionKey.includes('escalated');
          const borderColor = isJE ? 'var(--green)'
            : isApproval ? (actionKey.includes('approved') ? 'var(--green)' : 'var(--red)')
            : isResolution ? 'var(--navy-600)'
            : isEscalation ? 'var(--navy-800)'
            : a.actor_type === 'ai_agent' ? '#8b5cf6'
            : a.actor_type === 'human' ? 'var(--navy-700)'
            : 'var(--border-default)';

          const icon = ACTION_ICONS[actionKey] || '\u25CB';
          const newStatusColor = a.new_status ? STATUS_COLORS[a.new_status.toLowerCase()] || 'var(--text-tertiary)' : null;

          return (
            <div key={a.id || i} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border-default)',
              borderRadius: 6, padding: '10px 12px', borderLeft: `3px solid ${borderColor}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11 }}>{icon}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: isJE ? 'var(--green)' : isApproval && actionKey.includes('rejected') ? 'var(--red)' : '#fff' }}>
                    {(a.action || '').replace(/_/g, ' ').toUpperCase()}
                  </span>
                  {a.new_status && (
                    <span style={{ fontSize: 8, padding: '1px 6px', background: `${newStatusColor}15`, color: newStatusColor || 'var(--text-tertiary)', borderRadius: 3, fontWeight: 700, textTransform: 'uppercase' }}>
                      {a.new_status.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
                <span style={{ fontSize: 9, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                  {a.created_at ? new Date(a.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}
                </span>
              </div>
              {a.description && (
                <div style={{ fontSize: 10, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 2 }}>{cleanDescription(a.description)}</div>
              )}
              <div style={{ display: 'flex', gap: 12, fontSize: 9, color: 'var(--text-tertiary)' }}>
                <span style={{ color: a.actor_type === 'ai_agent' ? 'var(--navy-800)' : a.actor_type === 'human' ? 'var(--navy-600)' : 'var(--text-tertiary)' }}>
                  {a.actor_type}{a.actor_id ? `: ${a.actor_id}` : ''}
                </span>
                {a.confidence != null && (
                  <span>confidence: <span className="mono" style={{ fontWeight: 600 }}>{(a.confidence * 100).toFixed(0)}%</span></span>
                )}
                {a.previous_status && a.new_status && (
                  <span>{a.previous_status.replace(/_/g, ' ')} &rarr; {a.new_status.replace(/_/g, ' ')}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
